/*
 * Boot for /share/<gateway>/<share> — a board on its own, outside the app.
 *
 * A LINK carries its secret in the URL fragment (never sent to a server, never
 * in a log): the token is the credential, nobody signs in, the board opens. An
 * INVITE has no fragment: the person signs in as themselves — any account, in
 * no org — the gateway matches their email against the invite list and hands
 * back a token of their own, and from then on the board runs under that token
 * exactly as a link would.
 *
 * The backend worker is the app's own; it just holds a share token instead of
 * a session. Everything it can reach with it is decided by the gateway.
 */
import { Clerk } from '@clerk/clerk-js';
import { dark } from '@clerk/themes';
import { BackendClient } from './bridge/backend-client';
import { ModelManager } from './managers/model-manager';
import { applyVitesseBranding, SPLASH_GRADIENT, fatal, mountSignIn } from '@vitesse/base-ui';
import { t } from '@vitesse/base-ui/i18n';
import { CLERK_PUBLISHABLE_KEY } from '../core/env';
import { grantFromShare } from '../dashboards/runtime/share-scope';
import './shell/vitesse-shared-dashboard';

const SHARE_PREFIX = 'vs1.';

export function parseSharePath(pathname: string): { gatewayId: string; shareId: string } | null {
  const seg = pathname.split('/').filter(Boolean);
  if (seg.length !== 3 || seg[0] !== 'share') return null;
  return { gatewayId: seg[1], shareId: seg[2] };
}

function notice(root: HTMLElement, title: string, body: string): void {
  root.replaceChildren();
  const el = document.createElement('div');
  el.className = 'shared-board-notice';
  el.innerHTML = `<h1></h1><p></p>`;
  (el.firstElementChild as HTMLElement).textContent = title;
  (el.lastElementChild as HTMLElement).textContent = body;
  root.appendChild(el);
}

export async function bootShare(root: HTMLElement, pathname: string, hash: string): Promise<void> {
  applyVitesseBranding('Shared dashboard');
  const at = parseSharePath(pathname);
  if (!at) {
    notice(root, t('dashboards.shareGone'), t('dashboards.shareGoneHelp'));
    return;
  }
  const secret = hash.startsWith('#') ? hash.slice(1) : '';
  // The bearer the worker presents. A link starts with its token; an invite
  // starts with the person's session and switches to the token they are handed.
  let bearer: string | null = secret ? `${SHARE_PREFIX}${at.gatewayId}.${at.shareId}.${secret}` : null;
  let clerk: Clerk | undefined;
  const getToken = async (): Promise<string | null> => bearer ?? (clerk?.session ? clerk.session.getToken() : null);

  if (!secret) {
    clerk = new Clerk(CLERK_PUBLISHABLE_KEY);
    try {
      await clerk.load();
    } catch (err) {
      fatal(root, 'Auth failed to load', err);
      return;
    }
    if (!clerk.user) {
      mountSignIn(root, clerk as unknown as Parameters<typeof mountSignIn>[1], { background: SPLASH_GRADIENT, darkTheme: dark });
      clerk.addListener(() => {
        if (clerk?.user) location.reload(); // signed in: come back through the front door
      });
      return;
    }
  }

  const backend = await BackendClient.create(getToken, getToken);
  // A revoked or expired link fails at the door (the gateway refuses the token
  // itself), which arrives as a thrown error rather than a refused reply.
  let opened: Awaited<ReturnType<typeof backend.api.openShare>>;
  try {
    opened = await backend.api.openShare(at.gatewayId, at.shareId);
  } catch {
    opened = { ok: false, error: '' };
  }
  if (!opened.ok || !opened.share) {
    notice(root, t('dashboards.shareRefused'), opened.error || t('dashboards.shareGoneHelp'));
    return;
  }
  const share = opened.share;
  if (share.token) {
    bearer = share.token; // the invitee's own token from here on
    await backend.refreshToken();
  }
  const grant = grantFromShare(share.gatewayId, share.scope, share.expiresAtMs || null, share.allowInteraction);
  const models = share.scope.modelIds.length ? new ModelManager(backend, share.gatewayId) : undefined;
  if (models) void models.start();

  root.replaceChildren();
  const page = document.createElement('vitesse-shared-dashboard');
  page.share = share;
  page.grant = grant;
  page.backend = backend;
  page.models = models;
  root.appendChild(page);

  if (share.expiresAtMs) {
    const left = share.expiresAtMs - Date.now();
    setTimeout(() => notice(root, t('dashboards.shareExpired'), t('dashboards.shareExpiredHelp')), Math.max(0, Math.min(left, 2 ** 31 - 1)));
  }
}
